/**
 * AllInventoryUsageStepper
 * Bulk usage dialog — deduct inventory across all 6 types in one session.
 *
 * Step 0 — Select Location
 * Step 1 — Configure Usage Quantities & Submit
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, Box, Typography, IconButton, Stepper, Step, StepLabel, StepContent,
  Button, CircularProgress, TextField, Alert, useTheme, alpha
} from '@mui/material';
import { Close as CloseIcon, Remove as RemoveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useInventoryItems } from '../../../shared/useInventoryData';
import { getUsageConfig } from '../../../Usage/usageConfigs';
import LocationSelectDialog from '../../../Usage/UsageInventoryStepper/LocationSelectDialog';
import BulkUsageItemsTable from './BulkUsageItemsTable';

// ── Constants ─────────────────────────────────────────────────────────────────

const INVENTORY_TYPES = ['products', 'chemicals', 'caps', 'bottles', 'labels', 'supplies'];

const STEPS = ['Select Location', 'Configure Usage'];

const mkEmpty = () => ({
  products: {}, chemicals: {}, caps: {}, bottles: {}, labels: {}, supplies: {}
});

// ── Main component ─────────────────────────────────────────────────────────────

export default function AllInventoryUsageStepper({
  open, onBack, onClose, companyId, locations, onSuccess
}) {
  const theme = useTheme();

  // ── All 6 item lists — called unconditionally (React hook rules) ───────────
  const { items: productItems  } = useInventoryItems('products',  companyId);
  const { items: chemicalItems } = useInventoryItems('chemicals', companyId);
  const { items: capsItems     } = useInventoryItems('caps',      companyId);
  const { items: bottlesItems  } = useInventoryItems('bottles',   companyId);
  const { items: labelsItems   } = useInventoryItems('labels',    companyId);
  const { items: suppliesItems } = useInventoryItems('supplies',  companyId);

  const allItems = useMemo(() => ({
    products:  productItems,
    chemicals: chemicalItems,
    caps:      capsItems,
    bottles:   bottlesItems,
    labels:    labelsItems,
    supplies:  suppliesItems,
  }), [productItems, chemicalItems, capsItems, bottlesItems, labelsItems, suppliesItems]);

  // ── State ──────────────────────────────────────────────────────────────────
  const [activeStep,         setActiveStep]         = useState(0);
  const [location,           setLocation]           = useState(null);
  const [locationSearch,     setLocationSearch]     = useState('');
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [availableLots,      setAvailableLots]      = useState(mkEmpty());
  const [selectedItems,      setSelectedItems]      = useState(mkEmpty());
  const [notes,              setNotes]              = useState('');
  const [loading,            setLoading]            = useState(false);
  const [error,              setError]              = useState(null);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setActiveStep(0);
      setLocation(null);
      setLocationSearch('');
      setAvailableLots(mkEmpty());
      setSelectedItems(mkEmpty());
      setNotes('');
      setLoading(false);
      setError(null);
      setLocationDialogOpen(false);
    }
  }, [open]);

  // Load all lots when entering step 1
  useEffect(() => {
    if (activeStep === 1 && location) loadAllLots();
  }, [activeStep, location]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load available lots per type ───────────────────────────────────────────
  const loadAllLots = async () => {
    setLoading(true);
    setError(null);
    try {
      const newLots = mkEmpty();

      for (const type of INVENTORY_TYPES) {
        const config = getUsageConfig(type);
        const items  = allItems[type] || [];

        for (const item of items) {
          const lots = await config.getInventoryItems(companyId, item.id, location.id);
          if (!lots?.length) continue;

          if (type === 'products') {
            for (const lot of lots) {
              const key = `${item.id}_${lot.variant_id || 'null'}`;
              if (!newLots.products[key]) newLots.products[key] = [];
              newLots.products[key].push(lot);
            }
          } else {
            newLots[type][item.id] = lots;
          }
        }
      }

      setAvailableLots(newLots);
    } catch (err) {
      console.error('Error loading lots:', err);
      setError('Failed to load available inventory. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Quantity change handler ─────────────────────────────────────────────────
  // Signature: (type, key, lotId, value, tier?)
  const handleQuantityChange = (type, key, lotId, value, tier = 'quantity', mode = 'container') => {
    if (type === 'products') {
      const lot = availableLots.products[key]?.find(l => l.id === lotId);
      if (!lot) return;

      const numValue = parseInt(value) || 0;
      const upc      = lot.units_per_case   || 1;
      const cpp      = lot.cases_per_pallet || 1;
      const budget   = lot.total_base_units || 0;
      const cur      = selectedItems.products[key]?.[lotId] || {};
      const curPal   = tier === 'pallets' ? 0 : (cur.pallets || 0);
      const curCas   = tier === 'cases'   ? 0 : (cur.cases   || 0);
      const curUnt   = tier === 'units'   ? 0 : (cur.units   || 0);

      let maxVal;
      if (tier === 'pallets') {
        maxVal = Math.max(0, Math.min(
          lot.pallet_quantity || 0,
          Math.floor(Math.max(0, budget - curCas * upc - curUnt) / (cpp * upc))
        ));
      } else if (tier === 'cases') {
        maxVal = Math.max(0, Math.min(
          (lot.case_quantity || 0) + (lot.pallet_quantity || 0) * cpp,
          Math.floor(Math.max(0, budget - curPal * cpp * upc - curUnt) / upc)
        ));
      } else {
        maxVal = Math.max(0, budget - curPal * cpp * upc - curCas * upc);
      }

      const qty = Math.max(0, Math.min(numValue, maxVal));
      setSelectedItems(prev => ({
        ...prev,
        products: {
          ...prev.products,
          [key]: {
            ...(prev.products[key] || {}),
            [lotId]: {
              ...(prev.products[key]?.[lotId] || { pallets: 0, cases: 0, units: 0 }),
              [tier]: qty,
              lot_number:   lot.lot_number,
              batch_number: lot.batch_number
            }
          }
        }
      }));
    } else {
      const lots = availableLots[type]?.[key] || [];
      const lot  = lots.find(l => l.id === lotId);
      if (!lot) return;

      let qty, maxQty;
      if (type === 'chemicals' && mode === 'measure') {
        const numValue = parseFloat(value) || 0;
        maxQty = lot.current_total_quantity || 0;
        qty    = Math.max(0, Math.min(numValue, maxQty));
      } else {
        const numValue = parseInt(value) || 0;
        maxQty = lot.current_quantity || 0;
        qty    = Math.max(0, Math.min(numValue, maxQty));
      }

      setSelectedItems(prev => ({
        ...prev,
        [type]: {
          ...prev[type],
          [key]: {
            ...(prev[type]?.[key] || {}),
            [lotId]: {
              quantity:     qty,
              quantityMode: type === 'chemicals' ? mode : undefined,
              lot_number:   lot.lot_number,
              batch_number: lot.batch_number,
              maxQuantity:  maxQty
            }
          }
        }
      }));
    }
  };

  // ── Derived counts ─────────────────────────────────────────────────────────
  const totalUsageItems = useMemo(() => {
    let count = 0;
    for (const type of INVENTORY_TYPES) {
      for (const lotMap of Object.values(selectedItems[type] || {})) {
        for (const lot of Object.values(lotMap)) {
          if (type === 'products') {
            if ((lot.pallets || 0) + (lot.cases || 0) + (lot.units || 0) > 0) count++;
          } else {
            if ((lot.quantity || 0) > 0) count++;
          }
        }
      }
    }
    return count;
  }, [selectedItems]);

  const hasAnyLots = useMemo(() =>
    INVENTORY_TYPES.some(t => Object.keys(availableLots[t] || {}).length > 0),
  [availableLots]);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      let totalSubmitted = 0;

      for (const type of INVENTORY_TYPES) {
        const config  = getUsageConfig(type);
        const typeSel = selectedItems[type] || {};
        const usages  = [];

        if (type === 'products') {
          for (const [key, lotMap] of Object.entries(typeSel)) {
            const [itemId, variantPart] = key.split('_');
            const variantId = variantPart === 'null' ? null : variantPart;
            for (const [lotId, lot] of Object.entries(lotMap)) {
              if ((lot.pallets || 0) + (lot.cases || 0) + (lot.units || 0) > 0) {
                usages.push({
                  itemId, variantId, lotId,
                  pallets:      lot.pallets      || 0,
                  cases:        lot.cases        || 0,
                  units:        lot.units        || 0,
                  lot_number:   lot.lot_number,
                  batch_number: lot.batch_number
                });
              }
            }
          }
        } else {
          for (const [key, lotMap] of Object.entries(typeSel)) {
            for (const [lotId, lot] of Object.entries(lotMap)) {
              if ((lot.quantity || 0) > 0) {
                usages.push({
                  itemId:       key,
                  lotId,
                  quantity:     lot.quantity,
                  quantityMode: lot.quantityMode || 'container',
                  lot_number:   lot.lot_number,
                  batch_number: lot.batch_number
                });
              }
            }
          }
        }

        if (usages.length > 0) {
          await config.executeUsage(companyId, { locationId: location.id, usages, notes });
          totalSubmitted += usages.length;
        }
      }

      if (totalSubmitted === 0) {
        setError('No usage quantities entered.');
        setLoading(false);
        return;
      }

      onSuccess?.(`Successfully recorded ${totalSubmitted} usage transaction${totalSubmitted !== 1 ? 's' : ''} from ${location.name}`);
      onClose();
    } catch (err) {
      console.error('Bulk usage submit error:', err);
      setError(err.message || 'Failed to record usage. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Sorted locations ───────────────────────────────────────────────────────
  const sortedLocations = useMemo(() => {
    if (!locations) return [];
    return [...locations].sort((a, b) => {
      const aP = !!(a.is_main || a.is_primary);
      const bP = !!(b.is_main || b.is_primary);
      if (aP && !bP) return -1;
      if (!aP && bP) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [locations]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={(_, reason) => { if (reason !== 'backdropClick') onClose(); }}
      disableEscapeKeyDown
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3, border: 'none',
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.95)}, ${alpha(theme.palette.grey[100], 0.95)})`,
          boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
          overflow: 'hidden', maxHeight: '92vh'
        }
      }}
    >
      {/* Header */}
      <Box sx={{
        p: 3,
        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            p: 1.5, borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <RemoveIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 500 }}>Bulk Record Usage</Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Deduct inventory across any types in one session
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onBack && (
            <IconButton onClick={onBack} sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }, transition: 'all 0.2s ease' }}>
              <ArrowBackIcon sx={{ fontSize: 24 }} />
            </IconButton>
          )}
          <IconButton onClick={onClose} sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.04)', transform: 'rotate(90deg)' }, transition: 'all 0.2s ease' }}>
            <CloseIcon sx={{ fontSize: 28 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Stepper body */}
      <Box sx={{ p: 0, maxHeight: 'calc(92vh - 120px)', overflowY: 'auto' }}>
        <Box sx={{ p: 3 }}>
          <Stepper activeStep={activeStep} orientation="vertical">

            {/* ── Step 0: Select Location ──────────────────────────────── */}
            <Step>
              <StepLabel>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>{STEPS[0]}</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Select the location to deduct inventory from
                </Typography>
                <Button
                  fullWidth variant="outlined"
                  onClick={() => setLocationDialogOpen(true)}
                  sx={{
                    justifyContent: 'flex-start', py: 2, px: 2.5,
                    minHeight: 56, borderRadius: 2, bgcolor: 'white',
                    textTransform: 'none', fontSize: '0.95rem', fontWeight: 500,
                    color: location ? 'text.primary' : 'text.secondary', mb: 3,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      bgcolor: 'rgba(255,255,255,0.95)'
                    }
                  }}
                >
                  {location ? location.name : 'Select Location'}
                </Button>
                <Button
                  variant="contained" disabled={!location}
                  onClick={() => setActiveStep(1)}
                  sx={{ borderRadius: 2, px: 5, py: 1.5, fontWeight: 500, fontSize: '1rem' }}
                >
                  Continue
                </Button>
              </StepContent>
            </Step>

            {/* ── Step 1: Configure Usage ──────────────────────────────── */}
            <Step>
              <StepLabel>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>{STEPS[1]}</Typography>
              </StepLabel>
              <StepContent>
                {loading && !hasAnyLots ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                      Deducting from: <strong>{location?.name}</strong>
                    </Typography>

                    {!hasAnyLots ? (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        No inventory available at {location?.name}
                      </Alert>
                    ) : (
                      <BulkUsageItemsTable
                        availableLots={availableLots}
                        allItems={allItems}
                        selectedItems={selectedItems}
                        onQuantityChange={handleQuantityChange}
                      />
                    )}

                    <TextField
                      fullWidth multiline rows={2}
                      label="Notes (Optional)"
                      value={notes}
                      onChange={e => setNotes(e.target.value)}
                      sx={{ mt: 3, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
                    />

                    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                      <Button
                        onClick={() => setActiveStep(0)}
                        sx={{ borderRadius: 2, px: 4, py: 1.5, fontSize: '1rem' }}
                      >
                        Back
                      </Button>
                      <Button
                        variant="contained" color="error"
                        disabled={loading || totalUsageItems === 0}
                        onClick={handleSubmit}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                        sx={{ borderRadius: 2, px: 5, py: 1.5, fontWeight: 500, fontSize: '1rem' }}
                      >
                        {loading ? 'Submitting...' : `Submit Usage${totalUsageItems > 0 ? ` (${totalUsageItems})` : ''}`}
                      </Button>
                    </Box>
                  </>
                )}
              </StepContent>
            </Step>

          </Stepper>
        </Box>
      </Box>

      {/* Location picker dialog */}
      <LocationSelectDialog
        open={locationDialogOpen}
        onClose={() => { setLocationDialogOpen(false); setLocationSearch(''); }}
        title="Select Location"
        locations={sortedLocations}
        searchTerm={locationSearch}
        onSearchChange={setLocationSearch}
        onSelect={loc => { setLocation(loc); setLocationDialogOpen(false); setLocationSearch(''); }}
      />
    </Dialog>
  );
}
